<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Dropdown Component
      p.text--secondary A customizable dropdown component built with yz-menu, yz-list, yz-text-field, and yz-checkbox with consistent sizing and styling options.

    // Basic Usage
    .component-section.mb-8#basic-usage
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-usage')") Basic Usage
      .variation-item.mb-6
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('basic-usage')") Basic Dropdown
          p.text--secondary.caption Simple dropdown with default styling

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 400px;")
          .mb-3
            strong Selected: {{ JSON.stringify(basicValue) }}
          yz-dropdown(
            v-model="basicValue"
            :items="basicItems"
            label="Select an option"
            placeholder="Choose..."
          )

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === basicCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === basicCode ? 'success' : ''" @click="copyCode(basicCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ basicCode }}

    // Size Variations
    .component-section.mb-8#size-variations
      h3.mb-4.cursor-pointer(@click="navigateToSection('size-variations')") Size Variations
      .variation-item.mb-6
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('size-variations')") Different Sizes
          p.text--secondary.caption Available size options: sm (28px), md (32px), lg (36px - default)

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 400px;")
          .d-flex.flex-column.gap-4
            .d-flex.gap-4.align-center
              .flex-grow-1
                yz-dropdown(
                  v-model="sizeValues.sm"
                  :items="sizeItems"
                  label="Small (sm)"
                  size="sm"
                  placeholder="Small dropdown..."
                )
              .text-caption.text--secondary 28px height
            
            .d-flex.gap-4.align-center
              .flex-grow-1
                yz-dropdown(
                  v-model="sizeValues.md"
                  :items="sizeItems"
                  label="Medium (md)"
                  size="md"
                  placeholder="Medium dropdown..."
                )
              .text-caption.text--secondary 32px height
            
            .d-flex.gap-4.align-center
              .flex-grow-1
                yz-dropdown(
                  v-model="sizeValues.lg"
                  :items="sizeItems"
                  label="Large (lg - default)"
                  size="lg"
                  placeholder="Large dropdown..."
                )
              .text-caption.text--secondary 36px height

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === sizeCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === sizeCode ? 'success' : ''" @click="copyCode(sizeCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ sizeCode }}

    // Item Types
    .component-section.mb-8#item-types
      h3.mb-4.cursor-pointer(@click="navigateToSection('item-types')") Item Types
      .variations-grid
        .variation-item.mb-6(v-for="variation in itemVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 400px;")
            .mb-3
              strong Selected: {{ JSON.stringify($data[variation.model]) }}
            yz-dropdown(
              v-model="$data[variation.model]"
              v-bind="variation.props"
            )

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // States and Features
    .component-section.mb-8#states-features
      h3.mb-4.cursor-pointer(@click="navigateToSection('states-features')") States and Features
      .variations-grid
        .variation-item.mb-6(v-for="variation in stateVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 400px;")
            .mb-3(v-if="variation.model")
              strong Selected: {{ JSON.stringify($data[variation.model]) }}
            yz-dropdown(
              v-model="$data[variation.model]"
              v-bind="variation.props"
            )

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Slot Examples
    .component-section.mb-8#slot-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('slot-examples')") Slot Examples
      .variations-grid
        .variation-item.mb-6(v-for="variation in slotVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 400px;")
            .mb-3(v-if="variation.model")
              strong Selected: {{ JSON.stringify($data[variation.model]) }}
            yz-dropdown(v-if="variation.id === 'custom-item-slot'"
              v-model="customItemValue"
              v-bind="variation.props"
            )
              template(v-slot:item="{ item, index }")
                .d-flex.align-center
                  v-icon.mr-2(small) {{ item.icon }}
                  span {{ item.text }}
            
            yz-dropdown(v-else-if="variation.id === 'custom-selection-slot'"
              v-model="customSelectionValue"
              v-bind="variation.props"
            )
              template(v-slot:selection="{ item, index }")
                v-chip.mr-1.mb-1(small color="primary")
                  span {{ item.text }}

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Width Control
    .component-section.mb-8#width-control
      h3.mb-4.cursor-pointer(@click="navigateToSection('width-control')") Width Control
      .variation-item.mb-6
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('width-control')") Width Examples
          p.text--secondary.caption Various width configurations using CSS units and predefined sizes

        .variation-preview.mb-4.pa-4.border.rounded
          .d-flex.flex-column.gap-4
            .d-flex.gap-4.align-center
              .text-caption.text--secondary(style="min-width: 120px;") Fixed 200px:
              yz-dropdown(
                v-model="widthValues.pixel"
                :items="basicItems"
                placeholder="200px width..."
                width="200px"
              )
            
            .d-flex.gap-4.align-center
              .text-caption.text--secondary(style="min-width: 120px;") Percentage 50%:
              yz-dropdown(
                v-model="widthValues.percent"
                :items="basicItems"
                placeholder="50% width..."
                width="50%"
              )
            
            .d-flex.gap-4.align-center
              .text-caption.text--secondary(style="min-width: 120px;") Full width:
              yz-dropdown(
                v-model="widthValues.full"
                :items="basicItems"
                placeholder="Full width..."
                width="full"
              )
            
            .d-flex.gap-4.align-center
              .text-caption.text--secondary(style="min-width: 120px;") 15rem units:
              yz-dropdown(
                v-model="widthValues.rem"
                :items="basicItems"
                placeholder="15rem width..."
                width="15rem"
              )
            
            .d-flex.gap-4.align-center
              .text-caption.text--secondary(style="min-width: 120px;") Fit content:
              yz-dropdown(
                v-model="widthValues.fitContent"
                :items="['Short', 'A bit longer text']"
                placeholder="Fit content..."
                width="fit-content"
              )

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === widthCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === widthCode ? 'success' : ''" @click="copyCode(widthCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ widthCode }}

    // v-form Validation Examples
    .component-section.mb-8#validation-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('validation-examples')") v-form Validation
      .variation-item.mb-6
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('validation-examples')") v-form Integration
          p.text--secondary.caption Modern validation with v-form integration, custom rules, and reactive validation

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 600px;")
          // Basic v-form example
          .mb-4
            h5.mb-2 Basic v-form with Required Validation
            .mb-2
              small.text--secondary Form Valid: {{ formValid ? 'Yes' : 'No' }} | Selected: {{ JSON.stringify(basicFormValue) }}
            v-form(ref="basicForm" v-model="formValid")
              yz-dropdown(
                v-model="basicFormValue"
                :items="validationItems"
                label="Select an option"
                placeholder="Choose..."
                required
                required-error-message="Please select an option"
                clearable
              )
              .mt-3.d-flex.gap-2
                yz-btn(label="Reset" size="sm" @click="resetBasicForm")
                yz-btn(label="Validate Form" size="sm" outlined @click="validateBasicForm")
              .mt-2(v-if="basicFormValidationResult")
                span.yz-regular-body-xs(:class="{ 'success--text': basicFormValidationResult === 'Valid', 'error--text': basicFormValidationResult === 'Invalid' }") {{ basicFormValidationResult }}

          v-divider.my-4

          // Custom rules example
          .mb-4
            h5.mb-2 Custom Validation Rules
            .mb-2
              small.text--secondary Selected: {{ JSON.stringify(customRulesValue) }}
            v-form(ref="customRulesForm" v-model="customRulesFormValid")
              yz-dropdown(
                v-model="customRulesValue"
                :items="validationItems"
                required,
                :rules="customDropdownRules"
                label="Custom Rules Dropdown"
                placeholder="Cannot select Option A..."
                clearable
              )
              .mt-3.d-flex.gap-2
                yz-btn(label="Reset" size="sm" @click="resetCustomRulesForm")
                yz-btn(label="Validate Form" size="sm" outlined @click="validateCustomRulesForm")
              .mt-2(v-if="customRulesValidationResult")
                span.yz-regular-body-xs(:class="{ 'success--text': customRulesValidationResult === 'Valid', 'error--text': customRulesValidationResult === 'Invalid' }") {{ customRulesValidationResult }}

          v-divider.my-4

          // Multiple selection with rules
          .mb-4
            h5.mb-2 Multiple Selection with Custom Rules
            .mb-2
              small.text--secondary Selected: {{ JSON.stringify(multipleRulesValue) }}
            v-form(ref="multipleRulesForm" v-model="multipleRulesFormValid")
              yz-dropdown(
                v-model="multipleRulesValue"
                :items="validationItems"
                :rules="multipleDropdownRules"
                label="Multiple with Rules"
                placeholder="Select 2-3 options..."
                multiple
              )
              .mt-3.d-flex.gap-2
                yz-btn(label="Reset" size="sm" @click="resetMultipleRulesForm")
                yz-btn(label="Validate Form" size="sm" outlined @click="validateMultipleRulesForm")
              .mt-2(v-if="multipleRulesValidationResult")
                span.yz-regular-body-xs(:class="{ 'success--text': multipleRulesValidationResult === 'Valid', 'error--text': multipleRulesValidationResult === 'Invalid' }") {{ multipleRulesValidationResult }}

          v-divider.my-4

          // Multiple selection with chips validation
          .mb-4
            h5.mb-2 Multiple Selection with Chips Validation
            .mb-2
              small.text--secondary Selected: {{ JSON.stringify(chipsMultipleRulesValue) }}
            v-form(ref="chipsMultipleRulesForm" v-model="chipsMultipleRulesFormValid")
              yz-dropdown(
                v-model="chipsMultipleRulesValue"
                :items="technologyItems"
                :rules="technologyRules"
                label="Select Technologies"
                placeholder="Choose technologies..."
                multiple
                chips
                clearable
              )
              .mt-3.d-flex.gap-2
                yz-btn(label="Reset" size="sm" @click="resetChipsMultipleRulesForm")
                yz-btn(label="Validate Form" size="sm" outlined @click="validateChipsMultipleRulesForm")
              .mt-2(v-if="chipsMultipleRulesValidationResult")
                span.yz-regular-body-xs(:class="{ 'success--text': chipsMultipleRulesValidationResult === 'Valid', 'error--text': chipsMultipleRulesValidationResult === 'Invalid' }") {{ chipsMultipleRulesValidationResult }}

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === vformValidationCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === vformValidationCode ? 'success' : ''" @click="copyCode(vformValidationCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ vformValidationCode }}

    // Optional Validation Examples
    .component-section.mb-8#optional-validation-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('optional-validation-examples')") Optional Validation Examples
      .variation-item.mb-6
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('optional-validation-examples')") Optional Dropdown Selection
          p.text--secondary.caption Optional fields that validate only when values are selected - useful for preferences, filters, and optional settings
        
        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 500px;")
          
          // Optional category selection
          .mb-6
            h5.mb-2 Optional category filter
            small.text--secondary Selected: {{ JSON.stringify(optionalCategoryValue) }}
            v-form.mt-2(ref="optionalCategoryForm")
              yz-dropdown(
                v-model="optionalCategoryValue"
                :items="categoryItems"
                :rules="optionalCategoryRules"
                label="Category (optional)"
                placeholder="Select a category..."
                clearable
              )
            
            .d-flex.gap-2.mt-3
              yz-btn(label="Reset" size="sm" @click="resetOptionalCategoryForm")
              yz-btn(label="Validate" @click="validateOptionalCategoryForm" size="sm" outlined)
              span.yz-regular-body-xs.ml-2(v-if="optionalCategoryValidationResult" :class="{ 'success--text': optionalCategoryValidationResult === 'Valid', 'error--text': optionalCategoryValidationResult === 'Invalid' }") {{ optionalCategoryValidationResult }}
          
          // Optional location selection
          .mb-6
            h5.mb-2 Optional location preference
            small.text--secondary Selected: {{ JSON.stringify(optionalLocationValue) }}
            v-form.mt-2(ref="optionalLocationForm")
              yz-dropdown(
                v-model="optionalLocationValue"
                :items="locationItems"
                :rules="optionalLocationRules"
                label="Location (optional)"
                placeholder="Choose location..."
                clearable
              )
            
            .d-flex.gap-2.mt-3
              yz-btn(label="Reset" size="sm" @click="resetOptionalLocationForm")
              yz-btn(label="Validate" @click="validateOptionalLocationForm" size="sm" outlined)
              span.yz-regular-body-xs.ml-2(v-if="optionalLocationValidationResult" :class="{ 'success--text': optionalLocationValidationResult === 'Valid', 'error--text': optionalLocationValidationResult === 'Invalid' }") {{ optionalLocationValidationResult }}
          
          // Optional priority selection
          .mb-6
            h5.mb-2 Optional priority level
            small.text--secondary Selected: {{ JSON.stringify(optionalPriorityValue) }}
            v-form.mt-2(ref="optionalPriorityForm")
              yz-dropdown(
                v-model="optionalPriorityValue"
                :items="priorityItems"
                :rules="optionalPriorityRules"
                label="Priority (optional)"
                placeholder="Set priority level..."
                clearable
              )
            
            .d-flex.gap-2.mt-3
              yz-btn(label="Reset" size="sm" @click="resetOptionalPriorityForm")
              yz-btn(label="Validate" @click="validateOptionalPriorityForm" size="sm" outlined)
              span.yz-regular-body-xs.ml-2(v-if="optionalPriorityValidationResult" :class="{ 'success--text': optionalPriorityValidationResult === 'Valid', 'error--text': optionalPriorityValidationResult === 'Invalid' }") {{ optionalPriorityValidationResult }}
        
        .variation-code
          .code-header.d-flex.align-center.mb-2
            span.font-weight-medium Code Examples
          
          // Optional category selection code
          .mb-4
            .d-flex.align-center.mb-1
              yz-btn(icon-only size="md" :icon="copiedCode === optionalCategoryCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === optionalCategoryCode ? 'success' : ''" @click="copyCode(optionalCategoryCode)")
              span.font-weight-medium.ml-2 Optional category selection
            pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ optionalCategoryCode }}
          
          .mb-4
            .d-flex.align-center.mb-1
              yz-btn(icon-only size="md" :icon="copiedCode === optionalLocationCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === optionalLocationCode ? 'success' : ''" @click="copyCode(optionalLocationCode)")
              span.font-weight-medium.ml-2 Optional location selection
            pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ optionalLocationCode }}
          
          .mb-4
            .d-flex.align-center.mb-1
              yz-btn(icon-only size="md" :icon="copiedCode === optionalPriorityCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === optionalPriorityCode ? 'success' : ''" @click="copyCode(optionalPriorityCode)")
              span.font-weight-medium.ml-2 Optional priority selection
            pre.code-block.pa-2.rounded.text-caption.ml-md-8(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ optionalPriorityCode }}

    // Readonly vs Disabled State Comparison
    .component-section.mb-8#readonly-disabled-comparison
      h3.mb-4.cursor-pointer(@click="navigateToSection('readonly-disabled-comparison')") Readonly vs Disabled State Comparison
      .variation-item.mb-6
        .variation-header.mb-3
          h4.cursor-pointer(@click="navigateToSection('readonly-disabled-comparison')") State Comparison
          p.text--secondary.caption Demonstrating the visual and functional differences between readonly and disabled states

        .variation-preview.mb-4.pa-4.border.rounded(style="max-width: 600px;")
          .d-flex.flex-column.gap-6
            // Single Selection Examples
            .mb-6
              h5.mb-3.text-primary Single Selection Examples
              .d-flex.flex-column.gap-4
                .mb-4
                  h6.mb-2 Normal (Interactive)
                  yz-dropdown(
                    v-model="basicValue"
                    :items="validationItems"
                    label="Normal Dropdown"
                    placeholder="Can interact and select..."
                    clearable
                  )

                .mb-4
                  h6.mb-2 Readonly State
                  small.text--secondary.d-block.mb-2 Prevents interaction but maintains normal visual appearance. Value: {{ JSON.stringify(readonlyFormValue) }}
                  yz-dropdown(
                    v-model="readonlyFormValue"
                    :items="validationItems"
                    label="Readonly Dropdown"
                    placeholder="Cannot interact..."
                    readonly
                    clearable
                  )

                .mb-4
                  h6.mb-2 Disabled State
                  small.text--secondary.d-block.mb-2 Prevents interaction with visual indicators (grayed out). Value: {{ JSON.stringify(disabledFormValue) }}
                  yz-dropdown(
                    v-model="disabledFormValue"
                    :items="validationItems"
                    label="Disabled Dropdown"
                    placeholder="Cannot interact..."
                    disabled
                    clearable
                  )

            v-divider

            // Multiple Selection Examples (without chips)
            .mb-6
              h5.mb-3.text-primary Multiple Selection Examples (without chips)
              .d-flex.flex-column.gap-4
                .mb-4
                  h6.mb-2 Normal (Interactive)
                  small.text--secondary.d-block.mb-2 Value: {{ JSON.stringify(multipleValue) }}
                  yz-dropdown(
                    v-model="multipleValue"
                    :items="validationItems"
                    label="Normal Multiple Dropdown"
                    placeholder="Can select multiple..."
                    multiple
                    clearable
                  )

                .mb-4
                  h6.mb-2 Readonly State
                  small.text--secondary.d-block.mb-2 Prevents interaction but maintains normal visual appearance. Value: {{ JSON.stringify(readonlyMultipleValue) }}
                  yz-dropdown(
                    v-model="readonlyMultipleValue"
                    :items="validationItems"
                    label="Readonly Multiple Dropdown"
                    placeholder="Cannot interact..."
                    multiple
                    readonly
                    clearable
                  )

                .mb-4
                  h6.mb-2 Disabled State
                  small.text--secondary.d-block.mb-2 Prevents interaction with visual indicators (grayed out). Value: {{ JSON.stringify(disabledMultipleValue) }}
                  yz-dropdown(
                    v-model="disabledMultipleValue"
                    :items="validationItems"
                    label="Disabled Multiple Dropdown"
                    placeholder="Cannot interact..."
                    multiple
                    disabled
                    clearable
                  )

            v-divider

            // Multiple Selection with Chips Examples
            .mb-6
              h5.mb-3.text-primary Multiple Selection with Chips Examples
              .d-flex.flex-column.gap-4
                .mb-4
                  h6.mb-2 Normal (Interactive)
                  small.text--secondary.d-block.mb-2 Value: {{ JSON.stringify(chipsMultipleValue) }}
                  yz-dropdown(
                    v-model="chipsMultipleValue"
                    :items="['React', 'Vue.js', 'Angular', 'Svelte', 'Express.js', 'Next.js']"
                    label="Normal Chips Dropdown"
                    placeholder="Can select multiple frameworks..."
                    multiple
                    chips
                    clearable
                  )

                .mb-4
                  h6.mb-2 Readonly State
                  small.text--secondary.d-block.mb-2 Prevents interaction but maintains normal visual appearance. Value: {{ JSON.stringify(readonlyChipsValue) }}
                  yz-dropdown(
                    v-model="readonlyChipsValue"
                    :items="['React', 'Vue.js', 'Angular', 'Svelte', 'Express.js', 'Next.js']"
                    label="Readonly Chips Dropdown"
                    placeholder="Cannot interact..."
                    multiple
                    chips
                    readonly
                    clearable
                  )

                .mb-4
                  h6.mb-2 Disabled State
                  small.text--secondary.d-block.mb-2 Prevents interaction with visual indicators (grayed out). Value: {{ JSON.stringify(disabledChipsValue) }}
                  yz-dropdown(
                    v-model="disabledChipsValue"
                    :items="['React', 'Vue.js', 'Angular', 'Svelte', 'Express.js', 'Next.js']"
                    label="Disabled Chips Dropdown"
                    placeholder="Cannot interact..."
                    multiple
                    chips
                    disabled
                    clearable
                  )

        .variation-code
          .code-header.d-flex.align-center.mb-2
            yz-btn(icon-only size="md" :icon="copiedCode === readonlyDisabledCode ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === readonlyDisabledCode ? 'success' : ''" @click="copyCode(readonlyDisabledCode)")
            span.font-weight-medium.ml-2 Code

          pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
            code {{ readonlyDisabledCode }}

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API
      
      // Props
      h4.mb-3 Props
      .props-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in componentProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}

      // Events
      h4.mb-3 Events
      .props-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Event
                th Description
                th Parameters
            tbody
              tr(v-for="event in componentEvents" :key="event.name")
                td.font-weight-medium.text-no-wrap {{ event.name }}
                td.single-line {{ event.description }}
                td.text--secondary.text-no-wrap {{ event.parameters }}

      // Slots
      h4.mb-3 Slots
      .props-table
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Slot Name
                th Description
            tbody
              tr(v-for="slot in componentSlots" :key="slot.name")
                td.font-weight-medium.text-no-wrap {{ slot.name }}
                td.single-line {{ slot.description }}
</template>

<script>
export default {
  name: "Dropdown",
  data() {
    return {
      copiedCode: null,
      basicValue: null,
      sizeValues: {
        sm: null,
        md: null,
        lg: null
      },
      stringValue: null,
      objectValue: null,
      multipleValue: [],
      disabledValue: null,
      readonlyValue: null,
      errorValue: null,
      clearableValue: null,
      clearableSingleValue: null,
      clearableMultipleValue: [],
      clearableChipsSingleValue: null,
      clearableChipsMultipleValue: [],
      customItemValue: null,
      returnObjectValue: null,
      returnObjectMultipleValue: [],
      chipsSingleValue: null,
      chipsMultipleValue: [],
      chipsReturnObjectMultipleValue: [],
      widthValues: {
        pixel: null,
        percent: null,
        full: null,
        rem: null,
        fitContent: null
      },
      basicItems: [
        'Option 1',
        'Option 2',
        'Option 3',
        'Option 4'
      ],
      sizeItems: [
        'Small',
        'Medium', 
        'Large'
      ],
      objectItems: [
        { text: 'California', value: 'CA' },
        { text: 'Florida', value: 'FL' },
        { text: 'Georgia', value: 'GA' },
        { text: 'Nebraska', value: 'NE' },
        { text: 'Texas', value: 'TX' }
      ],
      searchItems: [
        'Apple',
        'Banana',
        'Cherry',
        'Date',
        'Elderberry',
        'Fig',
        'Grape',
        'Honeydew',
        'Indian Plum',
        'Jackfruit'
      ],
      basicCode: `yz-dropdown(
  v-model="selectedValue"
  :items="items"
  label="Select an option"
  placeholder="Choose..."
)`,
      sizeCode: `// Small (28px height)
yz-dropdown(
  v-model="value"
  :items="items"
  label="Small"
  size="sm"
)

// Medium (32px height) 
yz-dropdown(
  v-model="value"
  :items="items"
  label="Medium"
  size="md"
)

// Large (36px height - default)
yz-dropdown(
  v-model="value"
  :items="items"
  label="Large"
  size="lg"
)`,
      widthCode: `// Fixed pixel width
yz-dropdown(
  v-model="value"
  :items="items"
  placeholder="200px width..."
  width="200px"
)

// Percentage width
yz-dropdown(
  v-model="value"
  :items="items"
  placeholder="50% width..."
  width="50%"
)

// Predefined full width
yz-dropdown(
  v-model="value"
  :items="items"
  placeholder="Full width..."
  width="full"
)

// CSS units (rem)
yz-dropdown(
  v-model="value"
  :items="items"
  placeholder="15rem width..."
  width="15rem"
)

// Fit content size
yz-dropdown(
  v-model="value"
  :items="items"
  placeholder="Fit content..."
  width="fit-content"
)`,
      itemVariations: [
        {
          name: 'String Items',
          id: 'string-items',
          description: 'Simple array of strings as dropdown options',
          model: 'stringValue',
          props: {
            items: ['Option 1', 'Option 2', 'Option 3', 'Option 4'],
            label: 'String Items',
            placeholder: 'Select a string...'
          },
          code: `yz-dropdown(
  v-model="selectedValue"
  :items="['Option 1', 'Option 2', 'Option 3', 'Option 4']"
  label="String Items"
  placeholder="Select a string..."
)`
        },
        {
          name: 'Object Items',
          id: 'object-items',
          description: 'Array of objects with text and value properties',
          model: 'objectValue',
          props: {
            items: [
              { text: 'California', value: 'CA' },
              { text: 'Florida', value: 'FL' },
              { text: 'Georgia', value: 'GA' },
              { text: 'Nebraska', value: 'NE' },
              { text: 'Texas', value: 'TX' }
            ],
            label: 'Object Items',
            placeholder: 'Select a state...'
          },
          code: `yz-dropdown(
  v-model="selectedValue"
  :items="[
    { text: 'California', value: 'CA' },
    { text: 'Florida', value: 'FL' },
    { text: 'Georgia', value: 'GA' }
  ]"
  label="Object Items"
  placeholder="Select a state..."
)`
        },
        {
          name: 'Multiple Selection',
          id: 'multiple-selection',
          description: 'Allow selecting multiple items from the dropdown',
          model: 'multipleValue',
          props: {
            items: ['Apple', 'Banana', 'Cherry', 'Date', 'Elderberry', 'Fig', 'Grape', 'Honeydew', 'Indian Plum', 'Jackfruit'],
            label: 'Multiple Selection',
            placeholder: 'Select fruits...',
            multiple: true
          },
          code: `yz-dropdown(
  v-model="selectedValues"
  :items="['Apple', 'Banana', 'Cherry', 'Date', 'Elderberry', 'Fig', 'Grape', 'Honeydew', 'Indian Plum', 'Jackfruit']"
  label="Multiple Selection"
  placeholder="Select fruits..."
  multiple
)`
        },
        {
          name: 'Return Object (Single)',
          id: 'return-object-single',
          description: 'Returns the entire object when return-object is true, otherwise returns object[item-value]',
          model: 'returnObjectValue',
          props: {
            items: [
              { text: 'California', value: 'CA', code: 'US-CA' },
              { text: 'Florida', value: 'FL', code: 'US-FL' },
              { text: 'Georgia', value: 'GA', code: 'US-GA' },
              { text: 'Nebraska', value: 'NE', code: 'US-NE' },
              { text: 'Texas', value: 'TX', code: 'US-TX' }
            ],
            label: 'Return Object (true)',
            placeholder: 'Select a state...',
            returnObject: true
          },
          code: `yz-dropdown(
  v-model="selectedValue"
  :items="[
    { text: 'California', value: 'CA', code: 'US-CA' },
    { text: 'Florida', value: 'FL', code: 'US-FL' },
    { text: 'Georgia', value: 'GA', code: 'US-GA' }
  ]"
  label="Return Object"
  return-object
)`
        },
        {
          name: 'Return Object (Multiple)',
          id: 'return-object-multiple',
          description: 'Returns array of entire objects when return-object is true with multiple selection',
          model: 'returnObjectMultipleValue',
          props: {
            items: [
              { text: 'Red', value: 'red', hex: '#FF0000' },
              { text: 'Blue', value: 'blue', hex: '#0000FF' },
              { text: 'Green', value: 'green', hex: '#00FF00' },
              { text: 'Yellow', value: 'yellow', hex: '#FFFF00' }
            ],
            label: 'Return Object (Multiple)',
            placeholder: 'Select colors...',
            multiple: true,
            returnObject: true
          },
          code: `yz-dropdown(
  v-model="selectedValues"
  :items="[
    { text: 'Red', value: 'red', hex: '#FF0000' },
    { text: 'Blue', value: 'blue', hex: '#0000FF' },
    { text: 'Green', value: 'green', hex: '#00FF00' }
  ]"
  label="Return Object (Multiple)"
  multiple
  return-object
)`
        },
        {
          name: 'Chips (Single)',
          id: 'chips-single',
          description: 'Single selection displayed as a chip/badge instead of text',
          model: 'chipsSingleValue',
          props: {
            items: [
              { text: 'JavaScript', value: 'js', color: '#f7df1e' },
              { text: 'TypeScript', value: 'ts', color: '#3178c6' },
              { text: 'Python', value: 'py', color: '#3776ab' },
              { text: 'Go', value: 'go', color: '#00add8' },
              { text: 'Rust', value: 'rust', color: '#ce422b' }
            ],
            label: 'Programming Language (Chips)',
            placeholder: 'Select a language...',
            chips: true
          },
          code: `yz-dropdown(
  v-model="selectedValue"
  :items="[
    { text: 'JavaScript', value: 'js' },
    { text: 'TypeScript', value: 'ts' },
    { text: 'Python', value: 'py' }
  ]"
  label="Programming Language"
  chips
)`
        },
        {
          name: 'Chips (Multiple)',
          id: 'chips-multiple',
          description: 'Multiple selections displayed as individual chips/badges',
          model: 'chipsMultipleValue',
          props: {
            items: [
              { text: 'React', value: 'react' },
              { text: 'Vue.js', value: 'vue' },
              { text: 'Angular', value: 'angular' },
              { text: 'Svelte', value: 'svelte' },
              { text: 'Express.js', value: 'express' },
              { text: 'Next.js', value: 'nextjs' }
            ],
            label: 'Frameworks (Multiple Chips)',
            placeholder: 'Select frameworks...',
            multiple: true,
            chips: true
          },
          code: `yz-dropdown(
  v-model="selectedValues"
  :items="[
    { text: 'React', value: 'react' },
    { text: 'Vue.js', value: 'vue' },
    { text: 'Angular', value: 'angular' }
  ]"
  label="Frameworks"
  multiple
  chips
)`
        },
        {
          name: 'Chips + Return Object (Multiple)',
          id: 'chips-return-object-multiple',
          description: 'Multiple selections with chips that return full objects instead of values',
          model: 'chipsReturnObjectMultipleValue',
          props: {
            items: [
              { text: 'Senior Developer', value: 'senior', level: 5, salary: 120000 },
              { text: 'Mid Developer', value: 'mid', level: 3, salary: 80000 },
              { text: 'Junior Developer', value: 'junior', level: 1, salary: 50000 },
              { text: 'Tech Lead', value: 'lead', level: 6, salary: 150000 },
              { text: 'Principal Engineer', value: 'principal', level: 7, salary: 180000 }
            ],
            label: 'Job Roles (Chips + Return Object)',
            placeholder: 'Select roles...',
            multiple: true,
            chips: true,
            returnObject: true
          },
          code: `yz-dropdown(
  v-model="selectedValues"
  :items="[
    { text: 'Senior Developer', value: 'senior', level: 5, salary: 120000 },
    { text: 'Mid Developer', value: 'mid', level: 3, salary: 80000 },
    { text: 'Junior Developer', value: 'junior', level: 1, salary: 50000 }
  ]"
  label="Job Roles"
  multiple
  chips
  return-object
)`
        }
      ],
      stateVariations: [
        {
          name: 'Disabled',
          id: 'disabled',
          description: 'Disabled dropdown that cannot be interacted with and has visual indicators (grayed out)',
          model: 'disabledValue',
          props: {
            items: ['Option 1', 'Option 2', 'Option 3'],
            label: 'Disabled Dropdown',
            placeholder: 'Cannot select...',
            disabled: true
          },
          code: `yz-dropdown(
  v-model="selectedValue"
  :items="items"
  label="Disabled Dropdown"
  disabled
)`
        },
        {
          name: 'Readonly',
          id: 'readonly',
          description: 'Readonly dropdown that cannot be interacted with but maintains normal visual appearance',
          model: 'readonlyValue',
          props: {
            items: ['Option 1', 'Option 2', 'Option 3'],
            label: 'Readonly Dropdown',
            placeholder: 'Cannot interact...',
            readonly: true
          },
          code: `yz-dropdown(
  v-model="selectedValue"
  :items="items"
  label="Readonly Dropdown"
  readonly
)`
        },
        {
          name: 'Clearable (Single)',
          id: 'clearable-single',
          description: 'Single selection with clear functionality. Clear icon appears when there is a value.',
          model: 'clearableSingleValue',
          props: {
            items: ['React', 'Vue.js', 'Angular', 'Svelte'],
            label: 'Clearable (Single)',
            placeholder: 'Select a framework...',
            clearable: true
          },
          code: `yz-dropdown(
  v-model="selectedValue"
  :items="['React', 'Vue.js', 'Angular', 'Svelte']"
  label="Clearable (Single)"
  placeholder="Select a framework..."
  clearable
)`
        },
        {
          name: 'Clearable (Multiple)',
          id: 'clearable-multiple',
          description: 'Multiple selections with clear functionality. Clear icon appears to clear all selections.',
          model: 'clearableMultipleValue',
          props: {
            items: ['JavaScript', 'TypeScript', 'Python', 'Go', 'Rust', 'Java'],
            label: 'Clearable (Multiple)',
            placeholder: 'Select languages...',
            multiple: true,
            clearable: true
          },
          code: `yz-dropdown(
  v-model="selectedValues"
  :items="['JavaScript', 'TypeScript', 'Python', 'Go', 'Rust', 'Java']"
  label="Clearable (Multiple)"
  placeholder="Select languages..."
  multiple
  clearable
)`
        },
        {
          name: 'Clearable Chips (Single)',
          id: 'clearable-chips-single',
          description: 'Single selection with chips and clear functionality. Clear icon appears when there is a value.',
          model: 'clearableChipsSingleValue',
          props: {
            items: ['React', 'Vue.js', 'Angular', 'Svelte'],
            label: 'Clearable Chips (Single)',
            placeholder: 'Select a framework...',
            chips: true,
            clearable: true
          },
          code: `yz-dropdown(
  v-model="selectedValue"
  :items="['React', 'Vue.js', 'Angular', 'Svelte']"
  label="Clearable Chips (Single)"
  chips
  clearable
)`
        },
        {
          name: 'Clearable Chips (Multiple)',
          id: 'clearable-chips-multiple',
          description: 'Multiple selections with chips and clear functionality. Individual chips can be closed, plus clear icon for all.',
          model: 'clearableChipsMultipleValue',
          props: {
            items: ['JavaScript', 'TypeScript', 'Python', 'Go', 'Rust', 'Java'],
            label: 'Clearable Chips (Multiple)',
            placeholder: 'Select languages...',
            multiple: true,
            chips: true,
            clearable: true
          },
          code: `yz-dropdown(
  v-model="selectedValues"
  :items="['JavaScript', 'TypeScript', 'Python', 'Go', 'Rust', 'Java']"
  label="Clearable Chips (Multiple)"
  multiple
  chips
  clearable
)`
        }
      ],
      slotVariations: [
        {
          name: 'Custom Item Slot',
          id: 'custom-item-slot',
          description: 'Customize how items appear in the dropdown list with icons',
          model: 'customItemValue',
          props: {
            items: [
              { text: 'Home', value: 'home', icon: 'mdi-home' },
              { text: 'Profile', value: 'profile', icon: 'mdi-account' },
              { text: 'Settings', value: 'settings', icon: 'mdi-cog' },
              { text: 'Logout', value: 'logout', icon: 'mdi-logout' }
            ],
            label: 'Custom Item Dropdown',
            placeholder: 'Select a page...',
            itemText: 'text',
            itemValue: 'value'
          },
          itemSlot: {
            class: 'd-flex align-center',
            icon: 'item.icon',
            getText: (item) => item.text
          },
          code: `yz-dropdown(
  v-model="selectedValue"
  :items="menuItems"
  label="Custom Item Dropdown"
  item-text="text"
  item-value="value"
)
  template(v-slot:item="{ item, index }")
    .d-flex.align-center
      v-icon.mr-2(small) {{ item.icon }}
      span {{ item.text }}`
        },
      ],
      // v-form validation data
      formValid: false,
      basicFormValue: null,
      customRulesValue: null,
      customRulesFormValid: false,
      multipleRulesValue: [],
      multipleRulesFormValid: false,
      chipsMultipleRulesValue: [],
      chipsMultipleRulesFormValid: false,
      // Validation result messages
      basicFormValidationResult: null,
      customRulesValidationResult: null,
      multipleRulesValidationResult: null,
      chipsMultipleRulesValidationResult: null,
      validationItems: ['Option A', 'Option B', 'Option C', 'Option D'],
      technologyItems: ['Vue.js', 'React', 'Angular', 'Node.js', 'Python', 'Docker', 'AWS', 'TypeScript'],
      
      // Optional Validation Examples Data
      optionalCategoryValue: null,
      optionalLocationValue: null,
      optionalPriorityValue: null,
      optionalCategoryValidationResult: null,
      optionalLocationValidationResult: null,
      optionalPriorityValidationResult: null,

      // Readonly/Disabled State Examples Data
      readonlyFormValue: 'Option B',
      disabledFormValue: 'Option A',
      readonlyMultipleValue: ['Option A', 'Option C'],
      disabledMultipleValue: ['Option B', 'Option C'],
      readonlyChipsValue: ['React', 'Vue.js'],
      disabledChipsValue: ['Angular', 'Svelte'],
      categoryItems: ['Technology', 'Business', 'Healthcare', 'Education', 'Entertainment'],
      locationItems: ['New York', 'Los Angeles', 'Chicago', 'Houston', 'Phoenix', 'Philadelphia'],
      priorityItems: ['Low', 'Medium', 'High', 'Critical'],
      vformValidationCode: `// Basic v-form with required validation
v-form(ref="form" v-model="formValid")
  yz-dropdown(
    v-model="selectedValue"
    :items="items"
    label="Select an option"
    placeholder="Choose..."
    required
    required-error-message="Please select an option"
  )
  yz-btn(:disabled="!formValid" @click="submit") Submit

// Custom validation rules
yz-dropdown(
  v-model="selectedValue"
  :items="items"
  required
  :rules="[v => v !== 'Option A' || 'Option A is not allowed']"
  label="Custom Rules Dropdown"
)

// Multiple selection with rules
yz-dropdown(
  v-model="selectedValues"
  :items="items"
  :rules="[
    v => v.length >= 2 || 'Select at least 2 options',
    v => v.length <= 3 || 'Select maximum 3 options'
  ]"
  label="Multiple with Rules"
  multiple
)

// Multiple selection with chips validation
yz-dropdown(
  v-model="selectedTechnologies"
  :items="technologyItems"
  :rules="[v => v.length > 0 || 'Select at least one technology']"
  label="Select Technologies"
  multiple
  chips
  clearable
)`,
      componentProps: [
        {
          name: 'value',
          type: 'Any',
          default: 'null',
          description: 'The selected value(s) of the dropdown'
        },
        {
          name: 'size',
          type: 'String',
          default: 'lg',
          description: 'Size of the dropdown: "sm" (28px), "md" (32px), "lg" (36px)'
        },
        {
          name: 'items',
          type: 'Array',
          default: '[]',
          description: 'Array of items for the dropdown. Can be strings or objects with text/value properties'
        },
        {
          name: 'itemText',
          type: 'String | Function',
          default: 'text',
          description: 'Property name or function to get display text from items. For objects, uses this property name. For functions, receives item as parameter'
        },
        {
          name: 'itemValue',
          type: 'String | Function',
          default: 'value',
          description: 'Property name or function to get value from items. For objects, uses this property name. For functions, receives item as parameter'
        },
        {
          name: 'label',
          type: 'String',
          default: 'undefined',
          description: 'Label text displayed above the dropdown'
        },
        {
          name: 'placeholder',
          type: 'String',
          default: 'undefined',
          description: 'Placeholder text shown when no option is selected'
        },
        {
          name: 'multiple',
          type: 'Boolean',
          default: 'false',
          description: 'Allow multiple selections'
        },
        {
          name: 'disabled',
          type: 'Boolean',
          default: 'false',
          description: 'Disable the dropdown'
        },
        {
          name: 'appendIcon',
          type: 'String',
          default: '$yzi-chevron-down',
          description: 'Icon displayed at the end of the dropdown (chevron). Use $yzi-icon-name format for custom icons'
        },
        {
          name: 'fontClass',
          type: 'String',
          default: 'yz-regular-body-sm',
          description: 'Font class to apply to the dropdown text field'
        },
        {
          name: 'error',
          type: 'Boolean',
          default: 'false',
          description: 'Puts the dropdown in an error state'
        },
        {
          name: 'errorMessages',
          type: 'Array | String',
          default: '[]',
          description: 'Error messages to display below the dropdown'
        },
        {
          name: 'required',
          type: 'Boolean',
          default: 'false',
          description: 'Makes the dropdown selection mandatory'
        },
        {
          name: 'requiredErrorMessage',
          type: 'String',
          default: 'This field is required',
          description: 'Custom error message to show when required validation fails'
        },
        {
          name: 'rules',
          type: 'Array',
          default: '[]',
          description: 'Array of validation functions. Each function receives the value and returns either true (valid) or a string (error message)'
        },
        {
          name: 'clearable',
          type: 'Boolean',
          default: 'false',
          description: 'Adds a clear icon to remove all selections'
        },
        {
          name: 'returnObject',
          type: 'Boolean',
          default: 'false',
          description: 'When false (default), returns the value from object[item-value]. When true, returns the entire object'
        },
        {
          name: 'chips',
          type: 'Boolean',
          default: 'false',
          description: 'Display selections as removable chips/badges instead of text. Chips can be closed to remove selections'
        },
        {
          name: 'width',
          type: 'String',
          default: 'auto',
          description: 'Sets the width of the dropdown. Supports CSS units (px, %, rem, em, vw, vh), predefined sizes (full, fit-content), or auto for default width'
        },
        {
          name: 'readonly',
          type: 'Boolean',
          default: 'false',
          description: 'Makes the dropdown readonly (prevents interaction but maintains normal visual appearance, unlike disabled)'
        },
        {
          name: 'rules',
          type: 'Array',
          default: '[]',
          description: 'Array of validation functions for custom form validation. Each function receives the value and returns either true (valid) or a string (error message)'
        }
      ],
      componentEvents: [
        {
          name: 'input',
          description: 'Emitted when the selected value changes',
          parameters: 'value: Any'
        },
        {
          name: 'change',
          description: 'Emitted when the selection changes',
          parameters: 'value: Any'
        },
        {
          name: 'focus',
          description: 'Emitted when the dropdown receives focus',
          parameters: 'event: FocusEvent'
        },
        {
          name: 'blur',
          description: 'Emitted when the dropdown loses focus',
          parameters: 'event: FocusEvent'
        },
        {
          name: 'clear',
          description: 'Emitted when the clear icon is clicked (only when clearable is true)',
          parameters: 'none'
        }
      ],
      componentSlots: [
        {
          name: 'item',
          description: 'Customize how items are displayed in the dropdown list. Receives { item, index } scope'
        },
        {
          name: 'selection',
          description: 'Customize how selected items are displayed (only for multiple selection). Receives { item, index } scope'
        }
      ]
    }
  },
  computed: {
    // Custom validation rules
    customDropdownRules() {
      return [
        v => v !== 'Option A' || 'Option A is not allowed (try Option B, C, or D)'
      ]
    },
    multipleDropdownRules() {
      return [
        v => v.length >= 2 || 'Select at least 2 options',
        v => v.length <= 3 || 'Select maximum 3 options'
      ]
    },
    technologyRules() {
      return [
        v => v.length > 0 || 'Select at least one technology'
      ]
    },
    
    // Optional Validation Rules
    optionalCategoryRules() {
      return [
        v => !v || this.categoryItems.includes(v) || 'Please select a valid category'
      ]
    },
    optionalLocationRules() {
      return [
        v => !v || this.locationItems.includes(v) || 'Please select a valid location'
      ]
    },
    optionalPriorityRules() {
      return [
        v => !v || this.priorityItems.includes(v) || 'Please select a valid priority level'
      ]
    },
    
    // Code examples for optional validation
    optionalCategoryCode() {
      return `yz-dropdown(
  v-model="categoryFilter"
  :items="['Technology', 'Business', 'Healthcare', 'Education', 'Entertainment']"
  :rules="optionalCategoryRules"
  label="Category (optional)"
  placeholder="Select a category..."
  clearable
)

// Validation rule
optionalCategoryRules() {
  return [
    v => !v || this.categoryItems.includes(v) || 'Please select a valid category'
  ]
}`
    },
    optionalLocationCode() {
      return `yz-dropdown(
  v-model="locationPreference"
  :items="['New York', 'Los Angeles', 'Chicago', 'Houston', 'Phoenix', 'Philadelphia']"
  :rules="optionalLocationRules"
  label="Location (optional)"
  placeholder="Choose location..."
  clearable
)

// Validation rule
optionalLocationRules() {
  return [
    v => !v || this.locationItems.includes(v) || 'Please select a valid location'
  ]
}`
    },
    optionalPriorityCode() {
      return `yz-dropdown(
  v-model="priorityLevel"
  :items="['Low', 'Medium', 'High', 'Critical']"
  :rules="optionalPriorityRules"
  label="Priority (optional)"
  placeholder="Set priority level..."
  clearable
)

// Validation rule
optionalPriorityRules() {
  return [
    v => !v || this.priorityItems.includes(v) || 'Please select a valid priority level'
  ]
}`
    },
    readonlyDisabledCode() {
      return `// ===== SINGLE SELECTION EXAMPLES =====

// Normal (Interactive) Dropdown
yz-dropdown(
  v-model="selectedValue"
  :items="items"
  label="Normal Dropdown"
  placeholder="Can interact and select..."
  clearable
)

// Readonly State - maintains visual appearance, prevents interaction
yz-dropdown(
  v-model="readonlyValue"
  :items="items"
  label="Readonly Dropdown"
  placeholder="Cannot interact..."
  readonly
  clearable
)

// Disabled State - grayed out appearance, prevents interaction
yz-dropdown(
  v-model="disabledValue"
  :items="items"
  label="Disabled Dropdown"
  placeholder="Cannot interact..."
  disabled
  clearable
)

// ===== MULTIPLE SELECTION EXAMPLES (without chips) =====

// Normal Multiple Selection
yz-dropdown(
  v-model="selectedValues"
  :items="items"
  label="Normal Multiple Dropdown"
  placeholder="Can select multiple..."
  multiple
  clearable
)

// Readonly Multiple Selection
yz-dropdown(
  v-model="readonlyMultipleValues"
  :items="items"
  label="Readonly Multiple Dropdown"
  placeholder="Cannot interact..."
  multiple
  readonly
  clearable
)

// Disabled Multiple Selection
yz-dropdown(
  v-model="disabledMultipleValues"
  :items="items"
  label="Disabled Multiple Dropdown"
  placeholder="Cannot interact..."
  multiple
  disabled
  clearable
)

// ===== MULTIPLE SELECTION WITH CHIPS EXAMPLES =====

// Normal Multiple Selection with Chips
yz-dropdown(
  v-model="selectedChipsValues"
  :items="frameworkItems"
  label="Normal Chips Dropdown"
  placeholder="Can select multiple frameworks..."
  multiple
  chips
  clearable
)

// Readonly Multiple Selection with Chips
yz-dropdown(
  v-model="readonlyChipsValues"
  :items="frameworkItems"
  label="Readonly Chips Dropdown"
  placeholder="Cannot interact..."
  multiple
  chips
  readonly
  clearable
)

// Disabled Multiple Selection with Chips
yz-dropdown(
  v-model="disabledChipsValues"
  :items="frameworkItems"
  label="Disabled Chips Dropdown"
  placeholder="Cannot interact..."
  multiple
  chips
  disabled
  clearable
)`
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    navigateToSection(sectionId) {
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      } catch (err) {
        console.error('Failed to copy code:', err)
      }
    },
    // v-form validation methods
    validateBasicForm() {
      const isValid = this.$refs.basicForm.validate()
      this.basicFormValidationResult = isValid ? 'Valid' : 'Invalid'
      setTimeout(() => {
        this.basicFormValidationResult = null
      }, 1500)
    },
    resetBasicForm() {
      this.$refs.basicForm.resetValidation()
      this.basicFormValue = null
      this.basicFormValidationResult = null
    },
    validateCustomRulesForm() {
      const isValid = this.$refs.customRulesForm.validate()
      this.customRulesValidationResult = isValid ? 'Valid' : 'Invalid'
      setTimeout(() => {
        this.customRulesValidationResult = null
      }, 1500)
    },
    resetCustomRulesForm() {
      this.$refs.customRulesForm.resetValidation()
      this.customRulesValue = null
      this.customRulesValidationResult = null
    },
    validateMultipleRulesForm() {
      const isValid = this.$refs.multipleRulesForm.validate()
      this.multipleRulesValidationResult = isValid ? 'Valid' : 'Invalid'
      setTimeout(() => {
        this.multipleRulesValidationResult = null
      }, 1500)
    },
    resetMultipleRulesForm() {
      this.$refs.multipleRulesForm.resetValidation()
      this.multipleRulesValue = []
      this.multipleRulesValidationResult = null
    },
    validateChipsMultipleRulesForm() {
      const isValid = this.$refs.chipsMultipleRulesForm.validate()
      this.chipsMultipleRulesValidationResult = isValid ? 'Valid' : 'Invalid'
      setTimeout(() => {
        this.chipsMultipleRulesValidationResult = null
      }, 1500)
    },
    resetChipsMultipleRulesForm() {
      this.$refs.chipsMultipleRulesForm.resetValidation()
      this.chipsMultipleRulesValue = []
      this.chipsMultipleRulesValidationResult = null
    },
    
    // Optional Validation Methods
    validateOptionalCategoryForm() {
      this.$nextTick(() => {
        const formRefArray = this.$refs.optionalCategoryForm;
        const formRef = Array.isArray(formRefArray) ? formRefArray[0] : formRefArray;
        if (formRef && typeof formRef.validate === 'function') {
          const isValid = formRef.validate();
          this.optionalCategoryValidationResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.optionalCategoryValidationResult = null;
          }, 1500);
        }
      });
    },
    resetOptionalCategoryForm() {
      this.$nextTick(() => {
        const formRefArray = this.$refs.optionalCategoryForm;
        const formRef = Array.isArray(formRefArray) ? formRefArray[0] : formRefArray;
        if (formRef && typeof formRef.resetValidation === 'function') {
          formRef.resetValidation();
          this.optionalCategoryValidationResult = null;
          this.optionalCategoryValue = null;
        }
      });
    },
    validateOptionalLocationForm() {
      this.$nextTick(() => {
        const formRefArray = this.$refs.optionalLocationForm;
        const formRef = Array.isArray(formRefArray) ? formRefArray[0] : formRefArray;
        if (formRef && typeof formRef.validate === 'function') {
          const isValid = formRef.validate();
          this.optionalLocationValidationResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.optionalLocationValidationResult = null;
          }, 1500);
        }
      });
    },
    resetOptionalLocationForm() {
      this.$nextTick(() => {
        const formRefArray = this.$refs.optionalLocationForm;
        const formRef = Array.isArray(formRefArray) ? formRefArray[0] : formRefArray;
        if (formRef && typeof formRef.resetValidation === 'function') {
          formRef.resetValidation();
          this.optionalLocationValidationResult = null;
          this.optionalLocationValue = null;
        }
      });
    },
    validateOptionalPriorityForm() {
      this.$nextTick(() => {
        const formRefArray = this.$refs.optionalPriorityForm;
        const formRef = Array.isArray(formRefArray) ? formRefArray[0] : formRefArray;
        if (formRef && typeof formRef.validate === 'function') {
          const isValid = formRef.validate();
          this.optionalPriorityValidationResult = isValid ? 'Valid' : 'Invalid';
          setTimeout(() => {
            this.optionalPriorityValidationResult = null;
          }, 1500);
        }
      });
    },
    resetOptionalPriorityForm() {
      this.$nextTick(() => {
        const formRefArray = this.$refs.optionalPriorityForm;
        const formRef = Array.isArray(formRefArray) ? formRefArray[0] : formRefArray;
        if (formRef && typeof formRef.resetValidation === 'function') {
          formRef.resetValidation();
          this.optionalPriorityValidationResult = null;
          this.optionalPriorityValue = null;
        }
      });
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  background-color: #fafafa;
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}
</style>